import { useQuery } from "@tanstack/react-query";

export function useEffectiveTimezone(): string {
  const { data: userSettings } = useQuery<any>({ queryKey: ["/api/user-settings"] });
  const { data: tenantSettings } = useQuery<any>({ queryKey: ["/api/tenant-settings"] });
  const tenantTz = tenantSettings?.tenantBusinessSettings?.timezone || "UTC";
  if (userSettings?.timezoneIsCustom && userSettings?.timezone) {
    return userSettings.timezone;
  }
  return tenantTz;
}

/**
 * Returns "YYYY-MM-DD" for a given date in `tz`.
 * Uses formatToParts() — does NOT rely on en-CA locale output ordering.
 */
export function getDateStringInTimezone(tz: string, date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Adds `n` calendar days to `dateStr` (YYYY-MM-DD) using pure date arithmetic.
 * Safe across DST transitions — operates on UTC date components, not milliseconds.
 */
export function addCalendarDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

/**
 * Returns "YYYY-MM-DD" for the current date in the given timezone.
 * `offsetDays` shifts the result by that many calendar days (e.g. -1 = yesterday).
 * Uses addCalendarDays() — safe across DST transitions.
 */
export function dateStrInTz(tz: string, offsetDays = 0): string {
  try {
    const today = getDateStringInTimezone(tz);
    return offsetDays === 0 ? today : addCalendarDays(today, offsetDays);
  } catch {
    const today = new Date().toISOString().slice(0, 10);
    return offsetDays === 0 ? today : addCalendarDays(today, offsetDays);
  }
}

/**
 * Returns "YYYY-MM-DD" for Monday of the current week in the given timezone.
 * Uses date-string arithmetic — safe across DST transitions.
 */
export function weekStartStrInTz(tz: string): string {
  try {
    const todayStr = getDateStringInTimezone(tz);
    const [y, m, d] = todayStr.split("-").map(Number);
    // Use noon UTC on this calendar day to safely ask what weekday it is in `tz`
    const ref = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    const dow = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short" }).format(ref);
    const dayIndex = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(dow);
    const mondayOffset = dayIndex === 0 ? 6 : dayIndex - 1;
    return addCalendarDays(todayStr, -mondayOffset);
  } catch {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1;
    return addCalendarDays(now.toISOString().slice(0, 10), -diff);
  }
}

/**
 * Returns "YYYY-MM-01" for the first day of the current month in the given timezone.
 */
export function monthStartStrInTz(tz: string): string {
  try {
    const todayStr = getDateStringInTimezone(tz);
    return todayStr.slice(0, 8) + "01";
  } catch {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  }
}
